/**
 * Phase 27.38.5 — Notification sound utility.
 *
 * A tiny, dependency-free chime synthesized with the Web Audio API so
 * we don't have to ship an audio asset. Two small sine-wave tones (C6
 * then G6) with an exponential decay envelope — clocks in at ~220ms.
 *
 * Preference is stored in `localStorage` (per browser). Cross-device
 * sync isn't needed today; when it is, migrate to a user preference
 * column and mirror the reads here.
 */

const STORAGE_KEY = 'kr-notification-sound-enabled';
const VOLUME_KEY = 'kr-notification-sound-volume'; // 0..1
const DEFAULT_VOLUME = 0.35;

export function getNotificationSoundEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

export function setNotificationSoundEnabled(v) {
  try {
    localStorage.setItem(STORAGE_KEY, v ? 'true' : 'false');
  } catch (_) { /* private mode / storage denied */ }
}

export function getNotificationSoundVolume() {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (!raw) return DEFAULT_VOLUME;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : DEFAULT_VOLUME;
  } catch (_) {
    return DEFAULT_VOLUME;
  }
}

export function setNotificationSoundVolume(v) {
  try {
    localStorage.setItem(VOLUME_KEY, String(Math.max(0, Math.min(1, v))));
  } catch (_) { /* ignore */ }
}

// Keep a single AudioContext instance so rapid successive plays don't
// spawn hundreds of contexts (Chrome caps at ~6).
let _ctx = null;

function _getCtx() {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!_ctx) {
    try { _ctx = new AudioCtx(); } catch (_) { return null; }
  }
  // Some browsers suspend the context until a user gesture. Try to
  // resume on-demand — best effort, silently ignore failures.
  if (_ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

/**
 * Play a soft two-note chime (C6 → G6). Does nothing when the
 * preference is off. Returns a boolean so callers can log whether the
 * sound actually played (e.g. for a "test sound" button).
 */
export function playNotificationChime({ force = false } = {}) {
  if (!force && !getNotificationSoundEnabled()) return false;
  const ctx = _getCtx();
  if (!ctx) return false;
  const now = ctx.currentTime;
  const vol = getNotificationSoundVolume();
  // Master gain so we can share the same node across both tones.
  const master = ctx.createGain();
  master.gain.value = vol;
  master.connect(ctx.destination);

  const play = (freq, when, dur) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // Envelope: quick attack, exponential release.
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(1, when + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    osc.connect(g).connect(master);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  };

  play(1046.5, now, 0.18);        // C6
  play(1567.98, now + 0.11, 0.22); // G6
  return true;
}
